import React from "react";
import { FiZap, FiLayers, FiCode, FiActivity } from "react-icons/fi";
import { LuSettings, LuUsers, LuCircleCheck, LuSmartphone, LuShieldCheck, LuPercent, LuShield, LuClipboardList, LuCode, LuBug, LuRocket } from "react-icons/lu";

export const SolidAppHeroData = {
  backgroundImage: "/images/serviceBanners/solidBanner.webp",
  whiteImage: "/images/serviceBanners/solidW.webp",
  subTitle: "SolidJS Development Services",
  title: (
    <>
      Ultra-Fast <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Fine-Grained</span> Reactivity
    </>
  ),
  description:
    "We build ultra fast SolidJS applications using fine grained reactivity and optimized rendering architecture to deliver exceptional performance, minimal overhead, and highly responsive modern web experiences for scalable digital products.",
  primaryButton: {
    title: "Get a Free Quote",
    onClick: () => console.log("Solid Quote clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("Solid Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Fast Execution",
      desc: "Direct DOM updates for faster performance.",
      iconColor: "#4f88c6",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Fine-Grained UI",
      desc: "Only changed parts of UI re-render.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Compiled Output",
      desc: "Optimized JavaScript for maximum speed.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Zero Overhead",
      desc: "Minimal runtime for lightweight apps.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp",
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(79,136,198,0.35)",
      iconGlow: "rgba(79,136,198,0.9)",
    },
  ],
};
// 2. Stats Block
export const solidStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block
export const solidSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: "High-Performance SolidJS Solutions",
  buildCards: [
    {
      title: "Fast UI Systems",
      desc: "Build ultra-fast SolidJS interfaces optimized for real-time performance.",
      icon: FiActivity,
      colorClass: "text-[#4f88c6]",
      bgClass: "bg-[#4f88c6]/10",
      borderClass: "border-[#4f88c6]/20 hover:border-[#4f88c6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(79,136,198,0.25)]",
    },
    {
      title: "Real-Time Dashboards",
      desc: "Create live updating dashboards with instant data rendering.",
      icon: FiLayers,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Lightweight Architecture",
      desc: "Develop minimal and highly optimized application structures.",
      icon: FiCode,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Fine-Grained Reactivity",
      desc: "Update only required UI parts for maximum rendering efficiency.",
      icon: FiZap,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Signal State Flow",
      desc: "Manage state using signals for fast and predictable updates.",
      icon: FiActivity,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Next-Gen Web Apps",
      desc: "Build modern web applications using SolidJS reactive architecture.",
      icon: FiLayers,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "SOLIDJS OPTIMIZATION PROCESS",
  processTitle: "SolidJS Optimization Process",
  processSteps: [
    {
      step: "01",
      title: "Logic Design",
      description: "Design reactive architecture using Signals and Memos for efficient data flow.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Component Building",
      description: "Create components that compile directly into optimized DOM operations.",
      icon: LuCode,
    },
    {
      step: "03",
      title: "Performance Benchmarking",
      description: "Measure and ensure sub-millisecond rendering performance thresholds.",
      icon: LuBug,
    },
    {
      step: "04",
      title: "State Optimization",
      description: "Optimize reactive state updates for faster, low-latency UI responses.",
      icon: LuCode,
    },
    {
      step: "05",
      title: "Render Efficiency Tuning",
      description: "Reduce unnecessary re-renders for maximum execution speed benchmarks.",
      icon: LuCircleCheck, // Safe icon to prevent build failures
    },
    {
      step: "06",
      title: "Execution Profiling",
      description: "Analyze app performance and eliminate resource bottlenecks completely.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY SOLIDJS?",
  benefitsTitle: "Performance & Pre-compilation Advantages",
  benefits: [
    "Fine-grained reactivity",
    "Faster VDOM alternative",
    "Zero runtime overhead",
    "Signal-based data flow",
    "Small bundle size",
    "JSX + modern syntax",
    "Vite optimized build",
  ],
  logoImage: "/images/serviceBanners/solidBanner.webp",
  whiteLogoImage: "/images/serviceBanners/solidW.webp",
};

// 4. Portfolio Data
export const solidPortfolioData = {
  subtitle: "OUR CASE STUDIES",
  title: "Blazing Fast SolidJS Projects",
  buttonText: "View All Projects",
  buttonLink: "#",
  apps: [
    {
      title: "RealTime Monitor",
      badge: "Monitoring Tool",
      badgeColor: "bg-[#4f88c6]/10 text-[#4f88c6] border-[#4f88c6]/20",
      desc: "A sub-millisecond update dashboard for high-frequency data streams.",
      downloads: "Ultra Performance",
      rating: "5.0",
      image: "/images/custom-dev.jpg",
    },
  ],
};

// 5. Technologies Data
export const solidTechnologiesData = {
  techTitle: "SOLID ECOSYSTEM",
  technologies: [
    { name: "SolidJS", icon: "SolidJS" },
    { name: "Solid Router", icon: "Router" },
    { name: "Solid Start", icon: "Start" },
    { name: "Vite", icon: "Vite" },
    { name: "TypeScript", icon: "TypeScript" },
    { name: "Tailwind CSS", icon: "Tailwind" },
  ],
  trustTitle: "TRUSTED BY PERFORMANCE LEADERS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

// 6. About Data
export const solidAboutData = [
  {
    title: "SolidJS Development Services for High-Performance Web Applications",
    content: (
      <>
        Modern businesses need frontend technologies that deliver speed, responsiveness, and seamless user experiences without performance bottlenecks. Reliable{" "}
        <b className="text-text-primary font-semibold">solidjs development services</b> help organizations build lightweight and highly responsive applications designed for better
        scalability and faster interactions. Many businesses struggle with frontend systems that slow down as features become more complex. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, SolidJS solutions are developed around business goals, usability, and modern performance expectations
        to help companies create efficient digital experiences that support long-term growth.
      </>
    ),
  },
  {
    title: "SolidJS Development Company for Faster Digital Experiences",
    content: (
      <>
        A successful application depends on how efficiently it responds to user actions and manages real-time interactions. A trusted{" "}
        <b className="text-text-primary font-semibold">solidjs development company</b> helps businesses create smooth interfaces that improve usability, responsiveness, and overall
        customer engagement. Companies investing in <b className="text-text-primary font-semibold">solid js web development</b> often benefit from improved frontend efficiency
        because applications can process interactions with minimal delays. Tycho Technologies focuses on practical SolidJS frameworks designed to improve application performance
        while supporting scalable digital ecosystems.
      </>
    ),
  },
  {
    title: "SolidJS App Development Services for Better Scalability",
    content: (
      <>
        As businesses expand, digital products need architectures that remain stable and efficient under growing demand. Reliable{" "}
        <b className="text-text-primary font-semibold">solidjs app development services</b> help organizations create responsive applications that support faster updates and better
        interaction quality. Businesses looking to strengthen frontend innovation often align modern technologies with{" "}
        <a href="https://tychotechnologies.com/nextjs-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          next js development services
        </a>{" "}
        to improve digital flexibility and support scalable web experiences.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a SolidJS Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">solidjs development company in india</b> continues to grow as businesses seek lightweight and
        performance-focused frontend technologies. Companies looking to <b className="text-text-primary font-semibold">hire solidjs developers</b> often prioritize speed,
        maintainability, and smoother user experiences for long-term digital success. Businesses partnering with Tycho Technologies gain access to customized SolidJS strategies
        designed around measurable business goals and technical requirements. Many organizations also strengthen frontend performance through{" "}
        <a href="https://tychotechnologies.com/vuejs-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          vue js app development services
        </a>{" "}
        to create adaptable and future-ready digital products.
      </>
    ),
  },
];

// 7. FAQs
export const solidFaqs = [
  {
    q: "Why SolidJS over React?",
    a: "If performance is your absolute top priority, SolidJS's no-VDOM approach provides a significant edge over React by reducing memory usage and execution time.",
  },
  {
    q: "Is the JSX different from React?",
    a: "It's very similar, but SolidJS handles reactivity through signals rather than state re-renders, meaning the component function only runs once.",
  },
];

// Legacy Support
export const solidTechStack = ["SolidJS", "Solid Router", "Solid Start", "Vite", "TypeScript", "Tailwind CSS"];
